import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import SearchDropdown from '../../../../Components/Dropdown/dropdown';
import { Button } from 'primereact/button';

const getOptionId = (option) => {
  if (option === null || option === undefined) return undefined;
  if (typeof option !== 'object') return option;
  return option.value
    ?? option.id
    ?? option.employeeId
    ?? option.employee_id
    ?? option.projectId
    ?? option.project_id
    ?? option.project_seq_id
    ?? option.requestTypeId
    ?? option.request_type_id
    ?? option.req_access_type_id
    ?? option.access_type_id
    ?? option.aiToolId
    ?? option.ai_tool_id;
};

const getOptionLabel = (option) => {
  if (option === null || option === undefined) return '';
  if (typeof option !== 'object') return String(option);
  return option.label
    ?? option.name
    ?? option.employee_name
    ?? option.project_name
    ?? option.access_type_name
    ?? option.ai_tool_name
    ?? '';
};

const formatOptions = (options = []) => options.map((option) => ({
  label: getOptionLabel(option),
  value: getOptionId(option) ?? getOptionLabel(option),
}));

const splitValues = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim())
      .filter(Boolean);
  }
  return value === null || value === undefined || value === '' ? [] : [value];
};

const findOption = (options, id, label) => {
  const formattedOptions = formatOptions(options);
  const match = formattedOptions.find((option) => String(option.value) === String(id)
    || (label && option.label === label));
  if (match) return match;
  if (id === null || id === undefined || id === '') return null;
  return { label: label || String(id), value: id };
};

const addMissingSelections = (options, selections) => {
  const formattedOptions = formatOptions(options);
  const selected = Array.isArray(selections) ? selections : [selections];
  selected.forEach((selection) => {
    if (!selection) return;
    const selectedId = getOptionId(selection);
    if (!formattedOptions.some((option) => String(option.value) === String(selectedId))) {
      formattedOptions.push({ label: getOptionLabel(selection), value: selectedId });
    }
  });
  return formattedOptions;
};

const EditAiAccessReqModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  dropDownOptions,
  isLoading,
  isSubmitting,
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState([]);
  const [selectedRequestType, setSelectedRequestType] = useState(null);
  const [selectedAiTools, setSelectedAiTools] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [businessJustification, setBusinessJustification] = useState('');

  useEffect(() => {
    if (!open || !initialData) return;

    const employeeIds = splitValues(
      initialData.request_for_emp_id
      ?? initialData.employee_id
      ?? initialData.employee_ids
      ?? initialData.employeeId,
    );
    const employeeNames = splitValues(
      initialData.request_for_emp_name
      ?? initialData.employee_name
      ?? initialData.employee_names,
    );
    const aiToolNames = splitValues(initialData.ai_tool_names ?? initialData.aiToolNames);
    const aiToolIds = splitValues(initialData.ai_tool_ids ?? initialData.aiToolIds);
    const aiToolValues = aiToolIds.length ? aiToolIds : aiToolNames;

    setSelectedEmployee(employeeIds.map((id, index) => findOption(
      dropDownOptions.employee,
      id,
      employeeNames[index],
    )).filter(Boolean));
    setSelectedRequestType(findOption(
      dropDownOptions.accessRequestType,
      initialData.access_type_id ?? initialData.req_access_type_id ?? initialData.request_type_id,
      initialData.access_type_name ?? initialData.req_access_type_name ?? initialData.request_type_name,
    ));
    setSelectedAiTools(aiToolValues.map((id, index) => findOption(
      dropDownOptions.aiTool,
      id,
      aiToolNames[index],
    )).filter(Boolean));
    setSelectedProject(findOption(
      dropDownOptions.project,
      initialData.project_id ?? initialData.projectId,
      initialData.project_name ?? initialData.projectName,
    ));
    setBusinessJustification(
      initialData.business_justification
      ?? initialData.requester_comment
      ?? initialData.businessJustification
      ?? '',
    );
  }, [open, initialData, dropDownOptions]);

  const handleSubmit = () => onSubmit({
    employee: selectedEmployee[0],
    project: selectedProject,
    requestType: selectedRequestType,
    aiTools: selectedAiTools,
    businessJustification,
  });

  useEffect(() => {
    if (!open) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [open]);

  if (!open) return null;
  const employeeOptions = addMissingSelections(dropDownOptions.employee, selectedEmployee);
  const requestTypeOptions = addMissingSelections(
    dropDownOptions.accessRequestType,
    selectedRequestType,
  );
  const aiToolOptions = addMissingSelections(dropDownOptions.aiTool, selectedAiTools);
  const projectOptions = addMissingSelections(dropDownOptions.project, selectedProject);

  return (
    <div className="add-ai-access-req-modal-backdrop" onClick={onClose}>
      <div className="edit-ai-access-req-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-content-header">
          <h2>Edit AI Access Request</h2>
        </div>
        <div className="modal-content-body">
          <div className="edit-ai-access-req-input-wrapper">
            <label>
              <span className="required-label">Employee Name<span className="asteric">*</span></span>
            </label>
            <SearchDropdown
              options={employeeOptions}
              placeholder="Select Employee(s)"
              value={selectedEmployee}
              onChange={(event) => setSelectedEmployee(event.target.value)}
              multiSelect
              searchBox
              dropdownCss="edit-ai-access-req-search-dropdown"
              disabled
            />
          </div>
          <div className="edit-ai-access-req-input-wrapper">
            <label>
              <span className="required-label">Request Type<span className="asteric">*</span></span>
            </label>
            <SearchDropdown
              options={requestTypeOptions}
              placeholder="Select Request Type"
              value={selectedRequestType}
              onChange={(event) => setSelectedRequestType(event.target.value)}
              searchBox={false}
              dropdownCss="edit-ai-access-req-search-dropdown"
              disabled
            />
          </div>
          <div className="edit-ai-access-req-input-wrapper">
            <label>
              <span className="required-label">AI Tool Names<span className="asteric">*</span></span>
            </label>
            <SearchDropdown
              options={aiToolOptions}
              placeholder="Select AI Tool(s)"
              value={selectedAiTools}
              onChange={(event) => setSelectedAiTools(event.target.value)}
              multiSelect
              searchBox
              dropdownCss="edit-ai-access-req-search-dropdown"
              disabled={isLoading || isSubmitting}
            />
          </div>
          <div className="edit-ai-access-req-input-wrapper">
            <label>
              <span className="required-label">Project Name<span className="asteric">*</span></span>
            </label>
            <SearchDropdown
              options={projectOptions}
              placeholder="Select Project"
              value={selectedProject}
              onChange={(event) => setSelectedProject(event.target.value)}
              searchBox
              dropdownCss="edit-ai-access-req-search-dropdown"
              disabled
            />
          </div>
          <div className="edit-ai-access-req-input-wrapper">
            <label>
              <span className="required-label">Business Justification<span className="asteric">*</span></span>
            </label>
            <textarea
              className="edit-ai-access-req-textarea"
              value={businessJustification}
              onChange={(event) => setBusinessJustification(event.target.value)}
              disabled={isLoading || isSubmitting}
              required
            />
          </div>
        </div>
        <div className="modal-content-footer">
          <div className="footer-btn-wrapper">
            <Button label="Cancel" onClick={onClose} className="p-button-cancel" text disabled={isSubmitting} />
            <Button
              label="Submit"
              onClick={handleSubmit}
              className="p-button-submit"
              loading={isSubmitting}
              disabled={isLoading || isSubmitting}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

EditAiAccessReqModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  dropDownOptions: PropTypes.shape({
    employee: PropTypes.array,
    project: PropTypes.array,
    accessRequestType: PropTypes.array,
    aiTool: PropTypes.array,
  }).isRequired,
  isLoading: PropTypes.bool,
  isSubmitting: PropTypes.bool,
};

EditAiAccessReqModal.defaultProps = {
  initialData: null,
  isLoading: false,
  isSubmitting: false,
};

export default EditAiAccessReqModal;
